import React, { useState, useEffect, useRef } from 'react';

const SNIPPETS = {
  'Header Style': `/* Custom header */
.bb-admin-header {
  background: linear-gradient(135deg, var(--bb-primary), var(--bb-secondary));
}`,
  'Card Radius': `/* Rounded cards */
.bb-card, .bb-preview-card {
  border-radius: 16px;
}`,
  'Button Style': `/* Custom button */
.bb-btn-primary {
  border-radius: 24px;
  font-weight: 700;
}`,
  'Accent Background': `/* Accent section */
.bb-form-section {
  background: var(--bb-accent);
  border-radius: 12px;
  padding: 20px;
}`
};

function CustomCSSEditor({ customCSS = '', onUpdate, orgId, onSave, saving, savedMessage }) {
  const [css, setCSS] = useState(customCSS);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewActive, setPreviewActive] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    setCSS(customCSS);
    setHasChanges(false);
  }, [customCSS]);

  const handleChange = (e) => {
    setCSS(e.target.value);
    setHasChanges(e.target.value !== customCSS);
  };

  const applyChanges = () => {
    onUpdate(css);
    setHasChanges(false);
  };

  const resetChanges = () => {
    setCSS(customCSS);
    setHasChanges(false);
  };

  const togglePreview = () => {
    if (!previewActive) {
      const el = document.createElement('style');
      el.id = 'bb-custom-css-preview';
      el.textContent = css;
      document.head.appendChild(el);
    } else {
      const el = document.getElementById('bb-custom-css-preview');
      if (el) el.remove();
    }
    setPreviewActive(!previewActive);
  };

  const insertSnippet = (snippet) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const pos = ta.selectionStart;
    const newCSS = css.slice(0, pos) + '\n' + snippet + '\n' + css.slice(pos);
    setCSS(newCSS);
    setHasChanges(newCSS !== customCSS);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(pos + snippet.length + 2, pos + snippet.length + 2);
    }, 0);
  };

  const lineCount = css.split('\n').length;

  return (
    <div className="bb-editor">
      <div className="bb-editor-header">
        <div>
          <h2>Custom CSS</h2>
          <p className="bb-editor-desc">Advanced: inject custom CSS to fine-tune the portal appearance.</p>
        </div>
        <div className="bb-editor-actions">
          {savedMessage && <span className={`bb-save-msg ${savedMessage.includes('Error') ? 'error' : ''}`}>{savedMessage}</span>}
          <button className="bb-btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>

      {/* Quick insert */}
      <div className="bb-form-section">
        <h3 className="bb-section-title">Quick Insert</h3>
        <div className="bb-snippet-grid">
          {Object.entries(SNIPPETS).map(([name, snippet]) => (
            <button key={name} type="button" className="bb-snippet-btn" onClick={() => insertSnippet(snippet)}>
              {name}
            </button>
          ))}
        </div>
      </div>

      {/* CSS editor */}
      <div className="bb-form-section">
        <div className="bb-css-editor-header">
          <label htmlFor="cssTextarea">
            CSS Editor
            <span className="bb-css-lines">{lineCount} line{lineCount !== 1 ? 's' : ''}</span>
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button"
              className={`bb-btn-outline-sm ${previewActive ? 'active' : ''}`}
              onClick={togglePreview}>
              {previewActive ? '⏹ Stop Preview' : '▶ Preview Changes'}
            </button>
            {hasChanges && (
              <>
                <button type="button" className="bb-btn-outline-sm" onClick={resetChanges}>Reset</button>
                <button type="button" className="bb-btn-primary" onClick={applyChanges}>Apply Changes</button>
              </>
            )}
          </div>
        </div>
        <textarea
          id="cssTextarea" ref={textareaRef}
          className="bb-css-textarea"
          value={css} onChange={handleChange}
          rows={18}
          spellCheck={false}
          placeholder={`/* Add your custom CSS here */

/* Available CSS variables */
/* --bb-primary: #6B46C1       */
/* --bb-secondary: #0D9488     */
/* --bb-accent: #F3F0FF        */
/* --bb-primary-light: #EDE9FE */
/* --bb-dark: #1F2937          */
/* --bb-border: #E5E7EB        */

.bb-admin-header {
  /* your custom styles */
}`}
        />
        {previewActive && (
          <div className="bb-alert bb-alert--info">
            Preview mode active. CSS is applied live. Click "Stop Preview" to remove.
          </div>
        )}
      </div>

      {/* Reference */}
      <div className="bb-tips-box">
        <h4>Available CSS Variables</h4>
        <div className="bb-css-vars">
          {[
            ['--bb-primary',       '#6B46C1',  'Primary brand colour'],
            ['--bb-secondary',     '#0D9488',  'Secondary colour'],
            ['--bb-accent',        '#F3F0FF',  'Accent / highlight fill'],
            ['--bb-primary-light', '#EDE9FE',  'Light primary tint'],
            ['--bb-dark',          '#1F2937',  'Dark text'],
            ['--bb-gray',          '#6B7280',  'Muted text'],
            ['--bb-light-gray',    '#F9FAFB',  'Page background'],
            ['--bb-border',        '#E5E7EB',  'Border colour'],
            ['--org-primary',      'dynamic',  'Org-specific primary (from Brand Identity)']
          ].map(([name, val, desc]) => (
            <div key={name} className="bb-css-var-row">
              <code>{name}</code>
              <span className="bb-css-var-val">{val}</span>
              <span className="bb-css-var-desc">{desc}</span>
            </div>
          ))}
        </div>
        <ul style={{ marginTop: '12px' }}>
          <li>Use browser DevTools to inspect element class names</li>
          <li>Prefix your selectors with <code>.bb-admin</code> to target admin portal only</li>
          <li>Test on mobile before saving — check responsive breakpoints</li>
          <li>Use <code>!important</code> sparingly; prefer specificity</li>
        </ul>
      </div>
    </div>
  );
}

export default CustomCSSEditor;
